import {NgModule, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';

/* ===================== THIRD-PARTY MODULES ===================== */

import { DataTablesModule } from 'angular-datatables';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgChartsModule } from 'ng2-charts';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { OAuthModule } from 'angular-oauth2-oidc';
import { ToastrModule } from 'ngx-toastr';

import { ModalModule } from 'ngx-bootstrap/modal';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CarouselModule } from 'ngx-bootstrap/carousel';

import { DragDropModule } from '@angular/cdk/drag-drop';

/* ===================== SERVICES ===================== */

import { AppErrorHandler } from './app-error.handler';
import { AppTitleService } from './services/app-title.service';
import { AppTranslationService, TranslateLanguageLoader } from './services/app-translation.service';
import { ConfigurationService } from './services/configuration.service';
import { AlertService } from './services/alert.service';
import { ThemeManager } from './services/theme-manager';
import { LocalStoreManager } from './services/local-store-manager.service';
import { OidcHelperService } from './services/oidc-helper.service';
import { NotificationService } from './services/notification.service';
import { NotificationEndpoint } from './services/notification-endpoint.service';
import { AccountService } from './services/account.service';
import { AccountEndpoint } from './services/account-endpoint.service';
import { SfDataService } from './services/sf-data.service';
import { SFDataEndpoint } from './services/sf-data-endpoint.service';
import { PaymentRequestService } from './services/payment-request.service';
import { AgentService } from './services/agent.service';
import { AiHintStateService } from './services/ai-hint-state.service';
import { FieldIssueService } from './services/field-issue.service';

/* ===================== DIRECTIVES / PIPES ===================== */

import { EqualValidator } from './directives/equal-validator.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { BootstrapTabDirective } from './directives/bootstrap-tab.directive';
import { GroupByPipe } from './pipes/group-by.pipe';
import { PhoneNumberMaskDirective } from './phone-number-mask.directive';
import { FeinMaskDirective } from './fein-mask.directive';
import { AiHintDirective } from './directives/ai-hint.directive';
import { FieldIssueDirective } from './directives/field-issue.directive';

/* ===================== COMPONENTS ===================== */

import { AppComponent } from './components/app.component';
import { LoginComponent } from './components/login/login.component';
import { HomeComponent } from './components/home/home.component';
import { SettingsComponent } from './components/settings/settings.component';
import { AboutComponent } from './components/about/about.component';
import { NotFoundComponent } from './components/not-found/not-found.component';

import { BannerDemoComponent } from './components/controls/banner-demo.component';
import { TodoDemoComponent } from './components/controls/todo-demo.component';
import { StatisticsDemoComponent } from './components/controls/statistics-demo.component';
import { NotificationsViewerComponent } from './components/controls/notifications-viewer.component';
import { SearchBoxComponent } from './components/controls/search-box.component';
import { UserInfoComponent } from './components/controls/user-info.component';
import { UserPreferencesComponent } from './components/controls/user-preferences.component';
import { UsersManagementComponent } from './components/controls/users-management.component';
import { RolesManagementComponent } from './components/controls/roles-management.component';
import { RoleEditorComponent } from './components/controls/role-editor.component';

import { AppMainHeaderComponent } from './components/app-main-header/app-main-header.component';
import { AppMainFooterComponent } from './components/app-main-footer/app-main-footer.component';
import { AppMainSidebarComponent } from './components/app-main-sidebar/app-main-sidebar.component';
import { AppContentWrapperComponent } from './components/app-content-wrapper/app-content-wrapper.component';
import { AppControlSidebarComponent } from './components/app-control-sidebar/app-control-sidebar.component';
import { SfIcdReportComponent } from './components/sf-icd-report/sf-icd-report.component';
import { SfIcdCreateComponent } from './components/sf-icd-create/sf-icd-create.component';
import { SfIcdDetailComponent } from './components/sf-icd-detail/sf-icd-detail.component';
import { SfIcdInfoComponent } from './components/sf-icd-info/sf-icd-info.component';
import { SfIcdOffcCampComponent } from './components/sf-icd-offc-camp/sf-icd-offc-camp.component';
import { SfIcdAcknowledgeComponent } from './components/sf-icd-acknowledge/sf-icd-acknowledge.component';
import { SfIcdAddlocationComponent } from './components/sf-icd-addlocation/sf-icd-addlocation.component';
import { SfIcdCampmaintainComponent } from './components/sf-icd-campmaintain/sf-icd-campmaintain.component';
import { SfIcdCloselocationComponent } from './components/sf-icd-closelocation/sf-icd-closelocation.component';
import { SfIcdChangeOfficeInfoComponent } from './components/sf-icd-change-office-info/sf-icd-change-office-info.component';
import { SfIcdHubMaintainComponent } from './components/sf-icd-hub-maintain/sf-icd-hub-maintain.component';
import { SfIcdTerminateContractComponent } from './components/sf-icd-terminate-contract/sf-icd-terminate-contract.component';
import { SfIcdMoveLocationComponent } from './components/sf-icd-move-location/sf-icd-move-location.component';
import { SfIcdMoreComponent } from './components/sf-icd-more/sf-icd-more.component';
import { SfIcdCampaignmasterComponent } from './components/sf-icd-campaignmaster/sf-icd-campaignmaster.component';
import { SfHelpInformationComponent } from './components/sf-help-information/sf-help-information.component';
import { SfIcdChangerequestComponent } from './components/sf-icd-changerequest/sf-icd-changerequest.component';
import { SfIcdCampaignparticipationComponent } from './components/sf-icd-campaignparticipation/sf-icd-campaignparticipation.component';
import { SfIcdReopenicdComponent } from './components/sf-icd-reopenicd/sf-icd-reopenicd.component';

import { SfIcdInactiveComponent } from './components/sf-icd-inactive/sf-icd-inactive.component';
import { EmaillogsComponent } from './components/emaillogs/emaillogs.component';
import { HubReportUploadComponent } from './components/hub-report-upload/hub-report-upload.component';
import { EmailCompositionComponent } from './components/emaillogs/email-composition.component';
import { MfaSetupComponent } from './components/mfa-setup/mfa-setup.component';
import { ScpaymentsComponent } from './components/scpayments/scpayments.component';
import { AgentChatComponent } from './components/agent-chat/agent-chat.component';
import { AiHintPanelComponent } from './components/ai-hint-panel/ai-hint-panel.component';
import { PreSubmitReviewComponent } from './components/pre-submit-review/pre-submit-review.component';
import { SfDashboardAddressChangeTasksComponent } from './components/sf-dashboard-address-change-tasks/sf-dashboard-address-change-tasks.component';
import { HelpfulContactsMaintenanceComponent } from './components/helpful-contacts-maintenance/helpful-contacts-maintenance.component';
import { NetsuiteLedgerComponent } from './components/netsuite-ledger/netsuite-ledger.component';


@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        HomeComponent,
        SettingsComponent,
        AboutComponent,
        NotFoundComponent,
        UsersManagementComponent,
        UserInfoComponent,
        UserPreferencesComponent,
        RolesManagementComponent,
        RoleEditorComponent,
        NotificationsViewerComponent,
        SearchBoxComponent,
        StatisticsDemoComponent,
        TodoDemoComponent,
        BannerDemoComponent,
        EqualValidator,
        AutofocusDirective,
        BootstrapTabDirective,
        GroupByPipe,
        AppMainHeaderComponent, AppMainFooterComponent, AppMainSidebarComponent, AppContentWrapperComponent, AppControlSidebarComponent,
        SfIcdReportComponent, SfIcdCreateComponent, SfIcdDetailComponent, SfIcdInfoComponent, SfIcdOffcCampComponent, SfIcdAcknowledgeComponent,
        SfIcdAddlocationComponent, SfIcdCampmaintainComponent, SfIcdCloselocationComponent, SfIcdChangeOfficeInfoComponent, SfIcdTerminateContractComponent,
        SfIcdMoveLocationComponent, SfIcdMoreComponent, SfIcdCampaignmasterComponent,
        SfIcdChangerequestComponent, SfIcdCampaignparticipationComponent, SfIcdReopenicdComponent, PhoneNumberMaskDirective, FeinMaskDirective, SfIcdInactiveComponent,
  SfIcdHubMaintainComponent, EmaillogsComponent, EmailCompositionComponent, MfaSetupComponent, ScpaymentsComponent,
  AgentChatComponent, AiHintPanelComponent, PreSubmitReviewComponent, SfHelpInformationComponent,
    HubReportUploadComponent, SfDashboardAddressChangeTasksComponent, HelpfulContactsMaintenanceComponent, NetsuiteLedgerComponent,

    ],
    bootstrap: [AppComponent],
  exports: [AppMainHeaderComponent],
  imports: [BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        DataTablesModule,
        AppRoutingModule,
        DragDropModule,
        /* Third-party */
        DataTablesModule,
        NgSelectModule,
        NgxDatatableModule,
      NgChartsModule,
        ToastrModule.forRoot({
            positionClass: 'toast-top-right',
            closeButton: true,
            maxOpened: 100,
            preventDuplicates: true
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: TranslateLanguageLoader,
                deps: [HttpClient]
            }
        }),
        OAuthModule.forRoot(),
        TooltipModule.forRoot(),
        PopoverModule.forRoot(),
        BsDropdownModule.forRoot(),
        CarouselModule.forRoot(),
        ModalModule.forRoot(),
        AiHintDirective,
        FieldIssueDirective], providers: [
        { provide: ErrorHandler, useClass: AppErrorHandler },
        AlertService,
        ThemeManager,
        ConfigurationService,
        AppTitleService,
        AppTranslationService,
        NotificationService,
        NotificationEndpoint,
        AccountService,
        AccountEndpoint,
        SfDataService,
        SFDataEndpoint,
        PaymentRequestService,
        AgentService,
        AiHintStateService,
        FieldIssueService,
        LocalStoreManager,
        OidcHelperService,
        provideHttpClient(withInterceptorsFromDi())
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
